import { createSlice } from '@reduxjs/toolkit'
import { signOut } from 'store/auth'
import { FirestoreDatabase } from 'index'
import { Collection } from 'constants/index'
import { getDatesInRange } from 'utils'
import moment from 'moment-timezone'
import firebase from 'firebase/app'

export const initialState = {
  error: null,
  inProgress: false,
  attendeesAttendance: [],
  confFilter: '',
  currentPage: 0,
  pageSize: 10,
  conferenceTimezone: ''
}

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setConferenceFilter: (state, action) => {
      state.confFilter = action.payload
    },
    setAttendeesAttendance: (state, action) => {
      state.attendeesAttendance = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setAttendee: (state, action) => {
      state.attendee = action.payload
    },
    setConferenceTimezone: (state, action) => {
      state.conferenceTimezone = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.attendeesAttendance = []
      state.confFilter = ''
      state.currentPage = 0
      state.pageSize = 10
      state.conferenceTimezone = ''
    }
  }
})

const targetMapKey = (targetType, targetId) => `${targetType}_${targetId}`

export const fetchAttendeesAttendance = (conferenceId) => async (dispatch) => {
  try {
    dispatch(attendanceSlice.actions.setProgress(true))

    const attendeesSnap = await FirestoreDatabase.collection(Collection.ATTENDEES)
      .where('conferenceId', '==', conferenceId)
      .orderBy('lastName', 'asc')
      .get()

    const attendees = attendeesSnap.docs?.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }))

    const attendanceRecordsSnap = await FirestoreDatabase.collection(Collection.ATTENDANCE)
      .where('conferenceId', '==', conferenceId)
      .get()

    const attendanceRecords =
      attendanceRecordsSnap.docs?.map((doc) => ({
        id: doc?.id,
        ...doc?.data()
      })) || []

    const confSnap = await FirestoreDatabase.collection(Collection.CONFERENCES)
      .doc(conferenceId)
      .get()

    const conference = { id: confSnap.id, ...confSnap.data() }
    const confTimezone = conference?.timezone || 'America/Los_Angeles'

    const confDates = getDatesInRange(
      moment(conference?.startDate).tz(confTimezone).format('MM/DD/YYYY'),
      moment(conference?.endDate).tz(confTimezone).format('MM/DD/YYYY')
    )

    // Resolve every distinct event/session referenced by targetId/targetType in one batch of reads.
    const targetRefs = Array.from(
      new Map(
        attendanceRecords
          .filter((r) => r.targetId && r.targetType)
          .map((r) => [
            targetMapKey(r.targetType, r.targetId),
            { targetId: r.targetId, targetType: r.targetType }
          ])
      ).values()
    )
    const targetMap = new Map()
    await Promise.all(
      targetRefs.map(async ({ targetId, targetType }) => {
        const collectionName = targetType === 'event' ? Collection.EVENTS : Collection.SESSIONS
        try {
          const snap = await FirestoreDatabase.collection(collectionName).doc(targetId).get()
          if (snap.exists) {
            targetMap.set(targetMapKey(targetType, targetId), {
              id: snap.id,
              type: targetType,
              ...snap.data()
            })
          }
        } catch (err) {
          console.error('Failed to resolve attendance target', targetId, err)
        }
      })
    )

    // Resolve every distinct admin uid referenced by decidedBy/decisionHistory in one batch of reads.
    const adminUids = Array.from(
      new Set(
        attendanceRecords.flatMap((r) => [
          r.decidedBy,
          ...(r.decisionHistory || []).map((h) => h.decidedBy)
        ])
      ).values()
    ).filter(Boolean)
    const adminMap = new Map()
    await Promise.all(
      adminUids.map(async (uid) => {
        try {
          const snap = await FirestoreDatabase.collection(Collection.USERS).doc(uid).get()
          if (snap.exists) {
            adminMap.set(uid, snap.data()?.fullName || null)
          }
        } catch (err) {
          console.error('Failed to resolve admin', uid, err)
        }
      })
    )

    // Resolve the target referenced by supersededBy (points at another attendance doc id).
    const supersededByIds = Array.from(
      new Set(attendanceRecords.map((r) => r.supersededBy).filter(Boolean))
    )
    const supersedingRecordMap = new Map()
    if (supersededByIds.length) {
      const existingById = new Map(attendanceRecords.map((r) => [r.id, r]))
      await Promise.all(
        supersededByIds.map(async (attendanceId) => {
          let record = existingById.get(attendanceId)
          if (!record) {
            try {
              const snap = await FirestoreDatabase.collection(Collection.ATTENDANCE)
                .doc(attendanceId)
                .get()
              if (snap.exists) record = { id: snap.id, ...snap.data() }
            } catch (err) {
              console.error('Failed to resolve superseding attendance record', attendanceId, err)
            }
          }
          if (record) supersedingRecordMap.set(attendanceId, record)
        })
      )
    }

    const formatTime = (ts) => {
      if (!ts) return null
      if (typeof ts === 'object' && typeof ts.toDate === 'function') {
        return moment(ts.toDate()).tz(confTimezone)
      }
      return moment(ts).tz(confTimezone)
    }

    // Build map: "attendeeId_date" => attendance records array
    const attendanceMap = new Map()
    attendanceRecords.forEach((record) => {
      const target = targetMap.get(targetMapKey(record.targetType, record.targetId))
      const scanMoment = formatTime(record.scanTime)
      const date = scanMoment ? scanMoment.format('MM/DD/YYYY') : null
      if (!date) return

      const supersedingRecord = record.supersededBy
        ? supersedingRecordMap.get(record.supersededBy)
        : null
      const supersedingTarget = supersedingRecord
        ? targetMap.get(targetMapKey(supersedingRecord.targetType, supersedingRecord.targetId))
        : null

      const enrichedRecord = {
        id: record.id,
        targetId: record.targetId,
        targetType: record.targetType,
        targetTitle: target?.title || target?.name || 'Untitled',
        targetStart: target?.startDate ?? null,
        targetEnd: target?.endDate ?? null,
        scanTime: record.scanTime,
        scanTimeLabel: scanMoment ? scanMoment.format('h:mm A') : '',
        status: record.status,
        decidedBy: record.decidedBy ?? null,
        decidedByName: record.decidedBy ? adminMap.get(record.decidedBy) || null : null,
        decidedAt: record.decidedAt ?? null,
        decisionReason: record.decisionReason ?? '',
        decisionHistory: (record.decisionHistory || []).map((h) => ({
          ...h,
          decidedByName: h.decidedBy ? adminMap.get(h.decidedBy) || null : null
        })),
        supersededBy: record.supersededBy ?? null,
        supersedingTargetTitle: supersedingTarget?.title || supersedingTarget?.name || null
      }

      const key = `${record.attendeeId}_${date}`
      if (!attendanceMap.has(key)) attendanceMap.set(key, [])
      attendanceMap.get(key).push(enrichedRecord)
    })

    const attendeesWithAttendance = attendees.map((attendee) => {
      const attendeeAttendance = confDates.map((date) => {
        const records = (attendanceMap.get(`${attendee.id}_${date}`) ?? []).sort(
          (a, b) => (a.scanTime ?? 0) - (b.scanTime ?? 0)
        )
        const statusCounts = records.reduce(
          (acc, r) => {
            acc[r.status] = (acc[r.status] || 0) + 1
            return acc
          },
          { approved: 0, pending: 0, rejected: 0, superseded: 0 }
        )
        return {
          date,
          scanCount: records.length,
          scans: records.map((r) => r.scanTimeLabel).filter(Boolean),
          records,
          statusCounts
        }
      })
      return { ...attendee, attendance: attendeeAttendance }
    })

    dispatch(attendanceSlice.actions.setAttendeesAttendance(attendeesWithAttendance))
    dispatch(attendanceSlice.actions.setConferenceTimezone(confTimezone))
    dispatch(attendanceSlice.actions.setProgress(false))
  } catch (err) {
    console.error(err)
    dispatch(attendanceSlice.actions.setProgress(false))
    dispatch(attendanceSlice.actions.setError(err))
  }
}

// Client-side approve/reject write (the backend decideAttendance cloud function is
// intentionally left unwired for now).
export const decideAttendanceRecord = async (attendanceId, decision, adminUid, reason = '') => {
  const decidedAt = firebase.firestore.Timestamp.now()
  await FirestoreDatabase.collection(Collection.ATTENDANCE)
    .doc(attendanceId)
    .update({
      status: decision,
      decidedBy: adminUid || null,
      decidedAt,
      decisionReason: decision === 'rejected' ? reason || '' : '',
      decisionHistory: firebase.firestore.FieldValue.arrayUnion({
        decision,
        decidedBy: adminUid || null,
        decidedAt,
        reason: decision === 'rejected' ? reason || '' : ''
      })
    })
}

export default attendanceSlice
